import React, { useEffect, useState } from 'react';
import { listCompanies, listProducts, generateSalesKit } from '../salesKitAPI';

const TYPES = [
  { key: 'crmExcel', label: 'CRM Excel Data' },
  { key: 'emailMerge', label: 'Email Merge Sheet' },
  { key: 'quotation', label: 'Quotation Template' },
  { key: 'brochure', label: 'Product Brochure' },
  { key: 'whatsapp', label: 'WhatsApp Message' },
  { key: 'coldCallScript', label: 'Cold Calling Script' },
  { key: 'inventorySpec', label: 'Inventory Spec Sheet' },
  { key: 'nda', label: 'NDA Template' },
  { key: 'purchaseAgreement', label: 'Bulk Purchase Agreement' },
];

function toCSV(rows) {
  if (!rows || !rows.length) return '';
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
}

function download(filename, content, mime = 'text/plain') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function printHTML(title, bodyHTML) {
  const w = window.open('', '_blank');
  w.document.write(`<html><head><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;padding:24px;color:#111;} h1,h2{margin-bottom:4px;} pre{white-space:pre-wrap;font-family:inherit;}</style>
    </head><body>${bodyHTML}</body></html>`);
  w.document.close(); w.focus(); w.print();
}

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-xl text-xs font-semibold ring-1 transition text-left
        ${active ? 'bg-gradient-to-r from-violet-600/30 to-fuchsia-500/30 ring-violet-500/50 text-white' : 'bg-white/5 ring-white/10 text-white/50 hover:text-white/80'}`}
    >
      {children}
    </button>
  );
}

export default function GenerateKitPage({ onBack, onGenerated }) {
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(TYPES.map((t) => t.key));
  const [companySearch, setCompanySearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { listCompanies({ search: companySearch, limit: 20 }).then((d) => setCompanies(d.items)).catch(() => {}); }, [companySearch]);
  useEffect(() => { listProducts({ limit: 20 }).then((d) => setProducts(d.items)).catch(() => {}); }, []);

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const handleGenerate = async () => {
    if (!selectedCompanies.length) { setError('Select at least one company'); return; }
    setError(''); setLoading(true);
    try {
      const data = await generateSalesKit({ companyIds: selectedCompanies, productIds: selectedProducts, types: selectedTypes });
      setResult(data);
      onGenerated && onGenerated({ id: Date.now(), createdAt: new Date(), data, companyIds: selectedCompanies });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-white/50 hover:text-white transition">← Back</button>
          <h2 className="text-xl font-bold tracking-tight">Generate Sales Kit</h2>
          <span className="w-12" />
        </div>

        <Section step="1" title="Select Companies">
          <input
            placeholder="Search companies..."
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            className="w-full mb-3 bg-white/5 ring-1 ring-white/10 rounded-xl px-3 py-2 text-sm placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
            {companies.map((c) => (
              <label key={c._id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer transition">
                <input type="checkbox" checked={selectedCompanies.includes(c._id)} onChange={() => toggle(selectedCompanies, setSelectedCompanies, c._id)}
                  className="accent-violet-500 w-4 h-4" />
                <span className="text-sm">{c.businessName}</span>
                <span className="text-xs text-white/30 ml-auto">{c.city || 'n/a'}</span>
              </label>
            ))}
            {!companies.length && <p className="text-white/30 text-sm">No companies found.</p>}
          </div>
        </Section>

        <Section step="2" title="Select Products (optional)">
          <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
            {products.map((p) => (
              <label key={p._id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer transition">
                <input type="checkbox" checked={selectedProducts.includes(p._id)} onChange={() => toggle(selectedProducts, setSelectedProducts, p._id)}
                  className="accent-violet-500 w-4 h-4" />
                <span className="text-sm">{p.productName}</span>
              </label>
            ))}
            {!products.length && <p className="text-white/30 text-sm">No products found.</p>}
          </div>
        </Section>

        <Section step="3" title="What to Generate">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {TYPES.map((t) => (
              <Pill key={t.key} active={selectedTypes.includes(t.key)} onClick={() => toggle(selectedTypes, setSelectedTypes, t.key)}>
                {t.label}
              </Pill>
            ))}
          </div>
        </Section>

        {error && <p className="text-rose-400 text-sm mb-3">{error}</p>}

        <button
          onClick={handleGenerate}
          disabled={loading}
          className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500
                     hover:from-violet-500 hover:to-fuchsia-400 shadow-lg shadow-violet-900/30 disabled:opacity-50 transition"
        >
          {loading ? 'Generating…' : '⚡ Generate Sales Kit'}
        </button>

        {result && <ResultViewer result={result} />}
      </div>
    </div>
  );
}

function Section({ step, title, children }) {
  return (
    <div className="rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-6 h-6 rounded-full bg-violet-500/20 text-violet-300 text-xs font-bold flex items-center justify-center">{step}</span>
        <h4 className="text-sm font-semibold tracking-tight">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function OutputCard({ title, children }) {
  return (
    <div className="rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-5 mb-4">
      <h3 className="text-sm font-bold tracking-tight mb-3 text-violet-300">{title}</h3>
      {children}
    </div>
  );
}

const ghostBtn = 'px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 ring-1 ring-white/10 text-white/80 hover:bg-white/10 transition mr-2 mb-2';
const outputPre = 'whitespace-pre-wrap bg-black/30 ring-1 ring-white/5 rounded-xl p-3 text-xs text-white/70 max-h-64 overflow-y-auto mt-2';

function ResultViewer({ result }) {
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold tracking-tight mb-4">Results</h3>

      {result.crmExcel && (
        <OutputCard title="CRM Excel Data">
          <button className={ghostBtn} onClick={() => download('crm-data.csv', toCSV(result.crmExcel), 'text/csv')}>⬇ Download CSV</button>
          <pre className={outputPre}>{JSON.stringify(result.crmExcel, null, 2)}</pre>
        </OutputCard>
      )}

      {result.emailMerge && (
        <OutputCard title="Email Merge Sheet">
          <button className={ghostBtn} onClick={() => download('email-merge.csv', toCSV(result.emailMerge.rows), 'text/csv')}>⬇ Merge CSV</button>
          <button className={ghostBtn} onClick={() => download('email-template.txt', result.emailMerge.template)}>⬇ Template</button>
          <pre className={outputPre}>{result.emailMerge.template}</pre>
        </OutputCard>
      )}

      {result.quotation && (
        <OutputCard title="Quotation Template">
          <button
            className={ghostBtn}
            onClick={() => printHTML('Quotation', result.quotation.map((q) =>
              `<h2>Quotation - ${q.customer}</h2><p>Quotation No: ${q.quotationNo} | Date: ${q.date}</p>` +
              `<table border="1" cellpadding="6" style="border-collapse:collapse;width:100%"><tr><th>Product</th><th>Qty</th><th>Rate</th><th>GST</th><th>Total</th></tr>` +
              q.items.map((i) => `<tr><td>${i.product}</td><td>${i.qty}</td><td>${i.rate}</td><td>${i.gst}</td><td>${i.total}</td></tr>`).join('') +
              `</table><h4>Terms</h4><ul>${q.terms.map((t) => `<li>${t}</li>`).join('')}</ul><p>Signature: ____________________</p>`
            ).join('<hr/>'))}
          >
            🖨 Print / PDF
          </button>
          <pre className={outputPre}>{JSON.stringify(result.quotation, null, 2)}</pre>
        </OutputCard>
      )}

      {result.brochure && (
        <OutputCard title="Product Brochure">
          <button
            className={ghostBtn}
            onClick={() => printHTML('Brochure', result.brochure.map((b) =>
              `<h2>${b.companyIntro}</h2><h4>Products</h4><ul>${b.products.map((p) => `<li>${p.name} - ${p.specification || ''} (${p.unit || ''} - ₹${p.price || 0})</li>`).join('')}</ul>` +
              `<h4>Why work with us</h4><ul>${b.benefits.map((x) => `<li>${x}</li>`).join('')}</ul>` +
              `<p>${b.contact.contactPerson} | ${b.contact.phone} | ${b.contact.email} | ${b.contact.website}</p><p>${b.qrPlaceholder}</p>`
            ).join('<hr/>'))}
          >
            🖨 Print / PDF
          </button>
          <pre className={outputPre}>{JSON.stringify(result.brochure, null, 2)}</pre>
        </OutputCard>
      )}

      {result.whatsapp && (
        <OutputCard title="WhatsApp Message">
          {result.whatsapp.map((w) => (
            <div key={w.company} className="mb-3">
              <p className="text-sm font-semibold mb-1">{w.company}</p>
              <pre className={outputPre}>{w.message}</pre>
              <button className={ghostBtn} onClick={() => navigator.clipboard.writeText(w.message)}>📋 Copy</button>
            </div>
          ))}
        </OutputCard>
      )}

      {result.coldCallScript && (
        <OutputCard title="Cold Calling Script">
          {result.coldCallScript.map((c) => (
            <div key={c.company} className="mb-3">
              <p className="text-sm font-semibold mb-1">{c.company}</p>
              <pre className={outputPre}>{JSON.stringify(c.script, null, 2)}</pre>
            </div>
          ))}
        </OutputCard>
      )}

      {result.inventorySpec && (
        <OutputCard title="Inventory Specification Sheet">
          <button className={ghostBtn} onClick={() => download('inventory-spec.csv', toCSV(result.inventorySpec), 'text/csv')}>⬇ Download CSV</button>
          <pre className={outputPre}>{JSON.stringify(result.inventorySpec, null, 2)}</pre>
        </OutputCard>
      )}

      {result.nda && (
        <OutputCard title="NDA Template">
          {result.nda.map((n) => (
            <div key={n.company} className="mb-3">
              <p className="text-sm font-semibold mb-1">{n.company}</p>
              <button className={ghostBtn} onClick={() => download(`NDA-${n.company}.txt`, n.text)}>⬇ Download</button>
              <pre className={outputPre}>{n.text}</pre>
            </div>
          ))}
        </OutputCard>
      )}

      {result.purchaseAgreement && (
        <OutputCard title="Bulk Purchase Agreement">
          {result.purchaseAgreement.map((n) => (
            <div key={n.company} className="mb-3">
              <p className="text-sm font-semibold mb-1">{n.company}</p>
              <button className={ghostBtn} onClick={() => download(`Purchase-Agreement-${n.company}.txt`, n.text)}>⬇ Download</button>
              <pre className={outputPre}>{n.text}</pre>
            </div>
          ))}
        </OutputCard>
      )}
    </div>
  );
}
